import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLangPath } from "@/i18n/LangLayout";
import logo from "@/assets/logo.webp";

const practiceNavKeys = [
  { slug: "personal-injury", key: "practices.personalInjury.title" },
  { slug: "labor-law", key: "practices.laborLaw.title" },
  { slug: "social-security", key: "practices.socialSecurity.title" },
  { slug: "medical-negligence", key: "practices.medicalNegligence.title" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const lp = useLangPath();

  useEffect(() => {
    let ticking = false;
    const update = () => {
      const next = window.scrollY > 20;
      setScrolled(prev => (prev === next ? prev : next));
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isPracticeActive = location.pathname.includes("/practice/");

  const navClass = (isActive: boolean) => cn(
    "px-3 py-2 text-sm font-medium rounded-md transition-smooth",
    scrolled ? "text-foreground hover:text-accent" : "text-white/90 hover:text-accent",
    isActive && "text-accent"
  );

  return (
    <header className={cn(
      "fixed top-0 inset-x-0 z-50 transition-smooth",
      scrolled ? "bg-background/95 backdrop-blur-md shadow-card" : "bg-transparent"
    )}>
      <div className="container flex items-center justify-between h-20 lg:h-28">
        <Link to={lp("/")} className="flex items-center gap-2 group min-w-0">
          <img src={logo} alt={t("common.siteName")} className={cn("w-16 h-16 sm:w-20 sm:h-20 lg:w-20 lg:h-20 object-contain transition-smooth shrink-0", scrolled && "drop-shadow-[0_0_4px_rgba(0,0,0,0.4)]")} />
          <div className={cn("hidden sm:block leading-tight transition-smooth", scrolled ? "text-foreground" : "text-white")}>
            <div className="font-heading font-bold text-base">{t("common.siteName")}</div>
            <div className="text-[11px] opacity-80">{t("common.tagline")}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="ניווט ראשי">
          <NavLink to={lp("/")} end className={({ isActive }) => navClass(isActive)}>{t("nav.home")}</NavLink>
          <NavLink to={lp("/about")} className={({ isActive }) => navClass(isActive)}>{t("nav.about")}</NavLink>

          <DropdownMenu>
            <DropdownMenuTrigger className={cn(
              "px-3 py-2 text-sm font-medium rounded-md transition-smooth inline-flex items-center gap-1 outline-none",
              scrolled ? "text-foreground hover:text-accent" : "text-white/90 hover:text-accent",
              isPracticeActive && "text-accent"
            )}>
              {t("nav.practice")}
              <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              {practiceNavKeys.map(p => (
                <DropdownMenuItem key={p.slug} asChild>
                  <Link to={lp(`/practice/${p.slug}`)} className="cursor-pointer w-full">{t(p.key)}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink to={lp("/media")} className={({ isActive }) => navClass(isActive)}>{t("nav.media")}</NavLink>
          <NavLink to={lp("/blog")} className={({ isActive }) => navClass(isActive)}>{t("nav.blog")}</NavLink>
          <NavLink to={lp("/contact")} className={({ isActive }) => navClass(isActive)}>{t("nav.contact")}</NavLink>
          <LanguageSwitcher scrolled={scrolled} />
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a href="tel:0505380626">
            <Button variant="ghost" size="sm" className={cn(scrolled ? "" : "text-white hover:text-accent hover:bg-white/10")}>
              <Phone className="w-4 h-4 me-1" />
              050-5380626
            </Button>
          </a>
          <Link to={lp("/contact")}>
            <Button className="gradient-gold text-accent-foreground hover:opacity-90 shadow-gold border-0">
              {t("common.freeConsultation")}
            </Button>
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className={cn("lg:hidden p-2", scrolled ? "text-foreground" : "text-white")}
          aria-label="תפריט"
          aria-expanded={open}
          aria-controls="mobile-nav"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div id="mobile-nav" className="lg:hidden bg-background border-t border-border shadow-elegant">
          <div className="container py-4 flex flex-col gap-1">
            <LanguageSwitcher variant="mobile" />
            <NavLink to={lp("/")} end
              className={({ isActive }) => cn(
                "px-3 py-2.5 rounded-md text-sm font-medium transition-smooth",
                isActive ? "bg-secondary text-accent" : "text-foreground hover:bg-secondary"
              )}>
              {t("nav.home")}
            </NavLink>
            <NavLink to={lp("/about")}
              className={({ isActive }) => cn(
                "px-3 py-2.5 rounded-md text-sm font-medium transition-smooth",
                isActive ? "bg-secondary text-accent" : "text-foreground hover:bg-secondary"
              )}>
              {t("nav.about")}
            </NavLink>
            <NavLink to={lp("/media")}
              className={({ isActive }) => cn(
                "px-3 py-2.5 rounded-md text-sm font-medium transition-smooth",
                isActive ? "bg-secondary text-accent" : "text-foreground hover:bg-secondary"
              )}>
              {t("nav.media")}
            </NavLink>
            <NavLink to={lp("/blog")}
              className={({ isActive }) => cn(
                "px-3 py-2.5 rounded-md text-sm font-medium transition-smooth",
                isActive ? "bg-secondary text-accent" : "text-foreground hover:bg-secondary"
              )}>
              {t("nav.blog")}
            </NavLink>
            <NavLink to={lp("/contact")}
              className={({ isActive }) => cn(
                "px-3 py-2.5 rounded-md text-sm font-medium transition-smooth",
                isActive ? "bg-secondary text-accent" : "text-foreground hover:bg-secondary"
              )}>
              {t("nav.contact")}
            </NavLink>
            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2">{t("nav.practice")}</div>
            {practiceNavKeys.map(n => (
              <NavLink key={n.slug} to={lp(`/practice/${n.slug}`)}
                className={({ isActive }) => cn(
                  "px-3 py-2.5 rounded-md text-sm font-medium transition-smooth pr-6",
                  isActive ? "bg-secondary text-accent" : "text-foreground hover:bg-secondary"
                )}>
                {t(n.key)}
              </NavLink>
            ))}
            <a href="tel:0505380626" className="mt-3"><Button variant="outline" className="w-full"><Phone className="w-4 h-4 me-1" />050-5380626</Button></a>
            <Link to={lp("/contact")}><Button className="w-full gradient-gold text-accent-foreground border-0">{t("common.freeConsultation")}</Button></Link>
          </div>
        </div>
      )}
    </header>
  );
};
